import React, { useState } from 'react'
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import { AutoCoderEntity, AutoCoderImportBatch } from '../../../types/gotypes'
import { SendToPipelineTarget } from '../../../hooks/useAutoCoder'
import { entityDisplayName } from '../../../hooks/useEntityBrowser'
import { formatImportTimestamp } from '../../../utils/importBatches'
import AddDataDialog from './AddDataDialog'
import EntitiesTable from './EntitiesTable'
import ReceiptsTable from './ReceiptsTable'
import SendToPipelineDialog from './SendToPipelineDialog'

/**
 * The Data tab: everything this job HAS, before any pipeline has an opinion
 * about it.
 *
 * Two views of the same material — the imports that brought data in (and the
 * requests still waiting for their source), and the entities that data is
 * about — plus the one door that adds more. Only the
 * selected view is mounted, so the unselected one does not poll.
 */
export type DataView = 'receipts' | 'entities'

interface DataTabProps {
  customer: string
  job: string
  /** False while the shell is showing another tab. */
  active?: boolean
}

const DataTab: React.FC<DataTabProps> = ({ customer, job, active = true }) => {
  const [view, setView] = useState<DataView>('receipts')
  // What the send dialog is currently pointed at — null while it is closed, so
  // the dialog (and its pipelines query) only exists while somebody is sending.
  const [sendTarget, setSendTarget] = useState<SendToPipelineTarget | null>(null)
  const [sendDescription, setSendDescription] = useState<string | undefined>(undefined)
  const [addOpen, setAddOpen] = useState(false)

  const openSendForReceipt = (batch: AutoCoderImportBatch) => {
    setSendTarget({ importBatchId: batch.id })
    setSendDescription(
      `Everything the ${batch.source} fetch of ${formatImportTimestamp(batch.requestedAt)} produced`,
    )
  }

  // The entity door of the same send: every record filed against one video or
  // post, whichever fetch brought each of them in.
  const openSendForEntity = (entity: AutoCoderEntity) => {
    setSendTarget({ entityIds: [entity.id] })
    setSendDescription(`Every record about ${entityDisplayName(entity)}`)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ px: 3, pt: 3, flexShrink: 0 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          color="info"
          value={view}
          onChange={(_e, next: DataView | null) => {
            if (next) setView(next)
          }}
        >
          <ToggleButton value="receipts" data-testid="autocoder-data-view-receipts">
            Imports
          </ToggleButton>
          <ToggleButton value="entities" data-testid="autocoder-data-view-entities">
            Entities
          </ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        {/* The one door in: a file, a scraper or a survey variable, all of them
            producing a receipt in this job and none of them coding anything. */}
        <Button
          variant="contained"
          color="info"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
          data-testid="autocoder-data-add-button"
        >
          Add data
        </Button>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {view === 'receipts' && (
          <ReceiptsTable
            customer={customer}
            job={job}
            active={active}
            onSendToPipeline={openSendForReceipt}
          />
        )}
        {view === 'entities' && (
          <EntitiesTable
            customer={customer}
            job={job}
            active={active}
            onSendToPipeline={openSendForEntity}
          />
        )}
      </Box>

      <AddDataDialog
        open={addOpen}
        customer={customer}
        job={job}
        onClose={() => setAddOpen(false)}
        // Straight from "this fetch produced 3 000 records" to "code them" —
        // the same send flow a receipt row opens, pointed at the new receipt.
        onSendToPipeline={openSendForReceipt}
      />

      {sendTarget && (
        <SendToPipelineDialog
          customer={customer}
          job={job}
          target={sendTarget}
          targetDescription={sendDescription}
          onClose={() => {
            setSendTarget(null)
            setSendDescription(undefined)
          }}
        />
      )}
    </Box>
  )
}

export default DataTab
